/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { Box, Wizard, WizardStep } from '@factorialco/deprecated-design-system'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { TaskTypes } from 'modules/processes/hooks/types'

import { useFeaturesProvider } from 'modules/processes/components/ItemWizard/Providers/features'
import { useItemProvider } from 'modules/processes/components/ItemWizard/Providers/item'
import type { WizardData } from 'modules/processes/components/ItemWizard/Tasks/types'
import TaskSelectorHeader from './Header'
import {
  TaskComponent,
  taskSelectorOrder,
  useTaskSelectorComponents,
} from './useTaskSelectorComponents'

interface Props {
  data: WizardData
  onDataChanged: (newData: WizardData) => void
  setTitle: (title: string) => void
}

const TaskSelector = ({ data, onDataChanged, setTitle }: Props) => {
  const { loading } = useFeaturesProvider()
  const { category } = useParams()

  if (loading)
    return (
      <Box height="s400">
        <Wizard.Loading />
      </Box>
    )

  const { setItemType } = useItemProvider()
  const components = useTaskSelectorComponents()

  const onSelect = (newTaskType: TaskTypes) => {
    track(trackingEvents.processes.taskTypeWizardSelected, { taskType: newTaskType, category })
    onDataChanged({ ...data, taskType: newTaskType })
    setItemType(newTaskType)
  }

  useEffect(() => {
    if (data.taskType) {
      onSelect(data.taskType as TaskTypes)
    }

    setTitle(i18n.t('processes.process_settings.wizard.tasks.title'))
    onDataChanged({ ...data, currentStepIndex: 0 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <TaskSelectorHeader key={`${data.taskType}-header`} />
      <Box gap="s16" paddingY="s24" paddingRight="s16" key={data.taskType}>
        {taskSelectorOrder()
          .map((key) => components[key])
          .flatMap((Component: TaskComponent | undefined) => {
            if (!Component) return null

            return <Component key={Component.name} onSelect={onSelect} />
          })}
      </Box>
    </>
  )
}

export const taskSelectorStep = (): WizardStep<WizardData> => ({
  title: i18n.t('processes.process_settings.wizard.tasks.select_task.title'),
  nextText: i18n.t('common.next'),
  render: (props) => (
    <TaskSelector
      data={props.data}
      onDataChanged={props.onDataChanged}
      setTitle={props.data.setTitle}
    />
  ),
  isCompleted: (data) => !!data.taskType,
})
